"use client";

import { useEffect } from "react";
import {
  checkAccountStatus,
  checkOnboardingStatus,
  getRedirectPath,
} from "../lib/utils/redirect-utils";
import { useGetWhoAmIQuery } from "../lib/api/apiSlice";

export enum AccountStatus {
  ACTIVE = "active",
  UNDER_REVIEW = "under_review",
}

export enum OnboardingStatus {
  COMPLETED = "complete",
  PROCESSING = "processing",
  PENDING = "pending",
  ONBOARDING = "onboarding",
}

export interface WhoAmIUserData {
  user_id: string;
  full_name: string;
  email: string;
  phone_number: string;
  account_status: AccountStatus;
  onboarding_status?: OnboardingStatus;
  referral_code: string;
  referred_by: string;
  verified: boolean;
  created_at: string;
}


const WHOAMI_STORAGE_KEY = "whoami_user_data";
const REFERRAL_CODE_STORAGE_KEY = "referral_code";

export const useWhoAmI = () => {
  const referralCode: string | null = typeof window !== "undefined" 
    ? localStorage.getItem(REFERRAL_CODE_STORAGE_KEY)
    : null;

  const { data, error, isLoading, refetch } = useGetWhoAmIQuery(
    { referralCode: referralCode || undefined },
    { skip: false }
  );

  useEffect(() => {
    if (!data || !data.success || !data.data) {
      return;
    }
    const user = data.data;

    if (typeof window !== "undefined") {
      try {
        localStorage.setItem(WHOAMI_STORAGE_KEY, JSON.stringify(user));
        window.dispatchEvent(new Event("whoamiUpdated"));
      } catch (err) {
        console.error("Failed to update whoami data in localStorage", err);
      }
    }

    const accountStatus = checkAccountStatus(user);
    const onboardingStatus = checkOnboardingStatus(user);

    if (!accountStatus) return;

    if (typeof window === "undefined") return;

    const currentPath = window.location.pathname;
    const queryString = window.location.search.substring(1);
    const redirectPath = getRedirectPath(
      accountStatus,
      onboardingStatus,
      queryString
    );

    if (redirectPath) {
      const redirectPathOnly = redirectPath.split("?")[0];
      if (currentPath !== redirectPathOnly) {
        window.location.href = redirectPath;
      }
    }
  }, [data]);

  return {
    userData: data?.data,
    loading: isLoading,
    error: error ? (error as any).message || error : null,
    mutate: refetch,
  };
};
